import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CalendarClock, CheckCircle2, Loader2, LogOut, Mail, MessageSquare, Send, Star, X } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOrg } from '@/contexts/OrgContext';
import { useToast } from '@/hooks/use-toast';
import {
  useCustomerDashboardData,
  useSendCustomerMessage,
  useSaveCustomerProfile,
  useRescheduleCustomerBooking,
  useCancelCustomerBooking,
  useCancelCustomerEnquiry,
} from '@/hooks/useCustomerDashboard';
import { useCompanyInfoBrief } from '@/hooks/useCompanyInfo';
import { useSubmitReview } from '@/hooks/useCustomerReviews';
import type { Booking } from '@/hooks/useCustomerDashboard';

const errorMessage = (error: unknown, fallback = 'Please try again.') =>
  error && typeof error === 'object' && 'message' in error && typeof error.message === 'string'
    ? error.message
    : fallback;

const CustomerDashboard = () => {
  const { user, signOut } = useAuth();
  const { orgId, org } = useOrg();
  const { toast } = useToast();
  const { data: companyInfo } = useCompanyInfoBrief(orgId);
  const businessName = companyInfo?.company_name || org.name;
  const [email, setEmail] = useState('');
  const [linkSending, setLinkSending] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  // Deep-link support: email "View conversation" buttons land on /customer?thread=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  const threadParam = searchParams.get('thread');
  const tokenHash = searchParams.get('token_hash');
  const tokenType = searchParams.get('type');
  const [tab, setTab] = useState(threadParam ? 'messages' : 'bookings');
  const [reply, setReply] = useState('');
  const [magicLinkVerifying, setMagicLinkVerifying] = useState(
    () => Boolean(tokenHash && tokenType === 'magiclink' && !user)
  );
  const [magicLinkChecked, setMagicLinkChecked] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', address: '' });
  // Self-serve reschedule/cancel. Backed by customer-callable SECURITY DEFINER
  // RPCs (cancel_customer_booking / cancel_customer_enquiry /
  // reschedule_customer_booking) that mutate the row directly after re-checking
  // ownership, actionability and slot conflicts server-side. See
  // docs/customer-reschedule-cancel.md and the migration
  // 20260618030000_customer_reschedule_cancel_rpcs.sql.
  const [reschedulingId, setReschedulingId] = useState<string | null>(null);
  const [rescheduleForm, setRescheduleForm] = useState({ date: '', time: '' });
  // Customer-side reviews for completed jobs. Submissions reuse the existing
  // public-form path (customer_reviews, source 'public_form', is_approved false)
  // so the owner still moderates them in Admin > Reviews before they go public.
  // We snapshot the customer's name/location into the form but keep them
  // editable, and remember which jobs were reviewed this session to block an
  // obvious duplicate submit (the table has no per-job link, so this is
  // intentionally session-scoped — no schema change).
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, content: '', name: '', location: '' });
  const [reviewedBookingIds, setReviewedBookingIds] = useState<string[]>([]);
  const [cancelTarget, setCancelTarget] = useState<
    { kind: 'booking' | 'enquiry'; id: string; label: string } | null
  >(null);

  // Data query — gated on resolved auth session (user.id) + org. The magic-link
  // flow sets `user` via AuthContext's onAuthStateChange; this query will not fire
  // until both are present, preserving the original `if (!user || !orgId) return`
  // guard in the old loadCustomerData useEffect.
  const {
    data: dashboardData,
    isLoading: loading,
    error: dashboardError,
  } = useCustomerDashboardData(user?.id, orgId);

  const customers = useMemo(() => dashboardData?.customers ?? [], [dashboardData?.customers]);
  const enquiries = useMemo(() => dashboardData?.enquiries ?? [], [dashboardData?.enquiries]);
  const bookings = useMemo(() => dashboardData?.bookings ?? [], [dashboardData?.bookings]);
  const threads = useMemo(() => dashboardData?.threads ?? [], [dashboardData?.threads]);
  const messages = useMemo(() => dashboardData?.messages ?? [], [dashboardData?.messages]);

  // Mutations — all pass user?.id and orgId so their onSuccess can invalidate
  // the same query key.
  const sendMessageMutation = useSendCustomerMessage(user?.id, orgId);
  const saveProfileMutation = useSaveCustomerProfile(user?.id, orgId);
  const rescheduleMutation = useRescheduleCustomerBooking(user?.id, orgId);
  const cancelBookingMutation = useCancelCustomerBooking(user?.id, orgId);
  const cancelEnquiryMutation = useCancelCustomerEnquiry(user?.id, orgId);
  const submitReviewMutation = useSubmitReview();

  const actionPending =
    rescheduleMutation.isPending ||
    cancelBookingMutation.isPending ||
    cancelEnquiryMutation.isPending;

  const primaryCustomer = customers[0] ?? null;
  const activeThread = threads.find((thread) => thread.id === selectedThreadId) ?? threads[0] ?? null;
  const activeMessages = useMemo(
    () => messages.filter((message) => message.thread_id === activeThread?.id),
    [messages, activeThread?.id]
  );

  // When arriving from the Resend customer email, consume Supabase's hashed
  // magic-link token on /customer directly. This avoids relying on Supabase's
  // hosted verify redirect, which can fall back to the public site when the exact
  // tenant /customer URL is not allow-listed.
  useEffect(() => {
    if (!tokenHash || tokenType !== 'magiclink' || user || magicLinkChecked) return;

    let cancelled = false;
    setMagicLinkVerifying(true);

    supabase.auth
      .verifyOtp({ type: 'magiclink', token_hash: tokenHash })
      .then(({ error }) => {
        if (cancelled) return;
        if (error) {
          toast({ title: 'Could not open customer dashboard', description: error.message, variant: 'destructive' });
          return;
        }
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('token_hash');
        nextParams.delete('type');
        setSearchParams(nextParams, { replace: true });
      })
      .finally(() => {
        if (!cancelled) {
          setMagicLinkChecked(true);
          setMagicLinkVerifying(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [magicLinkChecked, searchParams, setSearchParams, toast, tokenHash, tokenType, user]);

  // When arriving via a ?thread=<id> deep link, select that thread once it loads.
  // Non-data effect — preserved as-is.
  useEffect(() => {
    if (threadParam && threads.some((thread) => thread.id === threadParam)) {
      setSelectedThreadId(threadParam);
    }
  }, [threadParam, threads]);

  // Sync profile form fields whenever the primary customer record changes.
  // Non-data effect — preserved as-is.
  useEffect(() => {
    if (!primaryCustomer) return;
    setProfileForm({
      name: primaryCustomer.name,
      phone: primaryCustomer.phone || '',
      address: primaryCustomer.address || '',
    });
  }, [primaryCustomer]);

  // Surface query errors via toast (mirrors the original try/catch in loadCustomerData).
  useEffect(() => {
    if (dashboardError) {
      console.error('Customer dashboard load failed:', dashboardError);
      toast({
        title: 'Could not load account',
        description: errorMessage(dashboardError),
        variant: 'destructive',
      });
    }
  }, [dashboardError, toast]);

  const sendMagicLink = async () => {
    if (!email.trim()) return;
    setLinkSending(true);
    const { error } = await supabase.functions.invoke('send-customer-magic-link', {
      body: { email: email.trim(), organizationId: orgId },
    });
    setLinkSending(false);

    if (error) {
      toast({ title: 'Could not send link', description: error.message, variant: 'destructive' });
      return;
    }

    toast({ title: 'Check your email', description: 'We sent a private sign-in link for your customer account.' });
  };

  const sendMessage = async () => {
    if (!reply.trim() || !activeThread) return;
    const customer = customers.find((row) => row.id === activeThread.customer_id);
    if (!customer) return;

    try {
      await sendMessageMutation.mutateAsync({
        threadId: activeThread.id,
        organizationId: orgId!,
        customerId: customer.id,
        senderUserId: user?.id,
        body: reply.trim(),
      });
      setReply('');
    } catch (error: unknown) {
      toast({ title: 'Could not send message', description: errorMessage(error), variant: 'destructive' });
    }
  };

  const saveProfile = async () => {
    if (!primaryCustomer || !profileForm.name.trim()) return;
    setProfileSaving(true);
    try {
      await saveProfileMutation.mutateAsync({
        customerId: primaryCustomer.id,
        name: profileForm.name,
        phone: profileForm.phone,
        address: profileForm.address,
      });
      toast({ title: 'Profile updated' });
    } catch (error: unknown) {
      toast({ title: 'Could not update profile', description: errorMessage(error), variant: 'destructive' });
    } finally {
      setProfileSaving(false);
    }
  };

  const startReschedule = (booking: Booking) => {
    setReschedulingId(booking.id);
    setRescheduleForm({ date: booking.scheduled_date, time: booking.start_time.slice(0, 5) });
  };

  // Directly reschedule the booking via the RPC. The server re-checks ownership,
  // that the booking is still actionable, and slot capacity; it preserves the
  // original duration and resets the booking to await re-confirmation.
  const submitReschedule = async (booking: Booking) => {
    if (!rescheduleForm.date || !rescheduleForm.time) return;
    try {
      await rescheduleMutation.mutateAsync({
        bookingId: booking.id,
        scheduledDate: rescheduleForm.date,
        startTime: rescheduleForm.time,
      });
      toast({ title: 'Booking rescheduled', description: 'Your new time is awaiting confirmation.' });
      setReschedulingId(null);
      setRescheduleForm({ date: '', time: '' });
    } catch (error: unknown) {
      toast({ title: 'Could not reschedule', description: errorMessage(error), variant: 'destructive' });
    }
  };

  const startReview = (booking: Booking) => {
    setReviewingId(booking.id);
    setReviewForm({
      rating: 5,
      content: '',
      name: primaryCustomer?.name ?? '',
      location: primaryCustomer?.address ?? '',
    });
  };

  // Submit a pending review for a completed job. Reuses the public-form insert
  // (source 'public_form', is_approved false) so the owner approves it in
  // Admin > Reviews before it shows on the homepage — never auto-approved.
  const submitReview = async (booking: Booking) => {
    if (!orgId || reviewForm.content.trim().length < 10) {
      toast({
        title: 'Add a few words about the job',
        description: 'Please write a little more before submitting your review.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await submitReviewMutation.mutateAsync({
        organization_id: orgId,
        customer_name: reviewForm.name.trim() || primaryCustomer?.name || 'Customer',
        customer_location: reviewForm.location.trim() || null,
        content: reviewForm.content.trim(),
        rating: reviewForm.rating,
      });
      setReviewedBookingIds((ids) => (ids.includes(booking.id) ? ids : [...ids, booking.id]));
      setReviewingId(null);
      toast({ title: 'Review submitted', description: 'Thanks — it will appear after approval.' });
    } catch (error: unknown) {
      toast({ title: 'Could not submit review', description: errorMessage(error), variant: 'destructive' });
    }
  };

  // Directly cancel the booking/enquiry via the RPC (same server-side guards).
  const confirmCancellation = async () => {
    if (!cancelTarget) return;
    const isBooking = cancelTarget.kind === 'booking';
    try {
      if (isBooking) {
        await cancelBookingMutation.mutateAsync({ bookingId: cancelTarget.id });
      } else {
        await cancelEnquiryMutation.mutateAsync({ enquiryId: cancelTarget.id });
      }
      toast({ title: isBooking ? 'Booking cancelled' : 'Enquiry cancelled' });
      setCancelTarget(null);
    } catch (error: unknown) {
      toast({ title: 'Could not cancel', description: errorMessage(error), variant: 'destructive' });
    }
  };

  if (!user && magicLinkVerifying) {
    return (
      <Layout>
        <main className="min-h-[70vh] bg-background px-4 py-16">
          <Card className="mx-auto max-w-md">
            <CardHeader>
              <CardTitle>Opening your customer dashboard</CardTitle>
              <CardDescription>Please wait while we verify your private link.</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Verifying secure link…
            </CardContent>
          </Card>
        </main>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <main className="min-h-[70vh] bg-background px-4 py-16">
          <Card className="mx-auto max-w-md">
            <CardHeader>
              <CardTitle>Access your account</CardTitle>
              <CardDescription>
                Enter the email you used for your enquiry or booking with {businessName}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer-email">Email address</Label>
                <Input
                  id="customer-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <Button className="w-full" onClick={sendMagicLink} disabled={linkSending}>
                {linkSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                Send my private link
              </Button>
            </CardContent>
          </Card>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="min-h-screen bg-background px-4 py-10 md:py-14">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="flex flex-col gap-4 rounded-lg border bg-card p-5 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{businessName}</p>
              <h1 className="text-2xl font-bold md:text-3xl">My bookings</h1>
            </div>
            <Button variant="outline" onClick={signOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>

          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : customers.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>No bookings found</CardTitle>
                <CardDescription>
                  We could not find enquiries or bookings for this email on {businessName}.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <Tabs value={tab} onValueChange={setTab} className="space-y-4">
              <TabsList>
                <TabsTrigger value="bookings">Bookings</TabsTrigger>
                <TabsTrigger value="enquiries">Enquiries</TabsTrigger>
                <TabsTrigger value="messages">Messages</TabsTrigger>
                <TabsTrigger value="profile">Profile</TabsTrigger>
              </TabsList>

              <TabsContent value="bookings" className="grid gap-4 md:grid-cols-2">
                {bookings.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-muted-foreground">No bookings yet.</CardContent>
                  </Card>
                ) : (
                  bookings.map((booking) => {
                    const canAct = !['cancelled', 'completed'].includes(booking.status);
                    return (
                      <Card key={booking.id}>
                        <CardHeader>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <CardTitle className="text-lg">{booking.title}</CardTitle>
                              <CardDescription>
                                {format(new Date(`${booking.scheduled_date}T${booking.start_time}`), 'dd MMM yyyy, HH:mm')}
                              </CardDescription>
                            </div>
                            <Badge variant="secondary" className="capitalize">{booking.status.replaceAll('_', ' ')}</Badge>
                          </div>
                        </CardHeader>
                        {booking.customer_notes && (
                          <CardContent className="pt-0 text-sm text-muted-foreground">
                            {booking.customer_notes}
                          </CardContent>
                        )}
                        {canAct && (
                          <CardContent className="space-y-3 pt-0">
                            {reschedulingId === booking.id ? (
                              <div className="space-y-3 rounded-md border p-3">
                                <div className="grid gap-3 sm:grid-cols-2">
                                  <div className="space-y-1.5">
                                    <Label htmlFor={`reschedule-date-${booking.id}`}>New date</Label>
                                    <Input
                                      id={`reschedule-date-${booking.id}`}
                                      type="date"
                                      value={rescheduleForm.date}
                                      onChange={(event) =>
                                        setRescheduleForm((current) => ({ ...current, date: event.target.value }))
                                      }
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor={`reschedule-time-${booking.id}`}>New time</Label>
                                    <Input
                                      id={`reschedule-time-${booking.id}`}
                                      type="time"
                                      value={rescheduleForm.time}
                                      onChange={(event) =>
                                        setRescheduleForm((current) => ({ ...current, time: event.target.value }))
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => submitReschedule(booking)}
                                    disabled={actionPending || !rescheduleForm.date || !rescheduleForm.time}
                                  >
                                    {actionPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Confirm new time
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={actionPending}
                                    onClick={() => {
                                      setReschedulingId(null);
                                      setRescheduleForm({ date: '', time: '' });
                                    }}
                                  >
                                    <X className="mr-2 h-4 w-4" />
                                    Keep current time
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                <Button size="sm" variant="outline" onClick={() => startReschedule(booking)}>
                                  <CalendarClock className="mr-2 h-4 w-4" />
                                  Reschedule
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setCancelTarget({ kind: 'booking', id: booking.id, label: booking.title })
                                  }
                                >
                                  Cancel booking
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        )}
                        {booking.status === 'completed' && (
                          <CardContent className="space-y-3 pt-0">
                            {reviewedBookingIds.includes(booking.id) ? (
                              <p className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                Review submitted — thanks! It'll appear once approved.
                              </p>
                            ) : reviewingId === booking.id ? (
                              <div className="space-y-3 rounded-md border p-3">
                                <div className="space-y-1.5">
                                  <Label>Your rating</Label>
                                  <div className="flex items-center gap-1" aria-label="Rating">
                                    {[1, 2, 3, 4, 5].map((rating) => (
                                      <button
                                        key={rating}
                                        type="button"
                                        onClick={() => setReviewForm((current) => ({ ...current, rating }))}
                                        className="rounded p-1"
                                        aria-label={`${rating} stars`}
                                      >
                                        <Star
                                          className={`h-5 w-5 ${
                                            rating <= reviewForm.rating
                                              ? 'fill-green-500 text-green-500'
                                              : 'text-muted-foreground'
                                          }`}
                                        />
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div className="space-y-1.5">
                                  <Label htmlFor={`review-content-${booking.id}`}>Your review</Label>
                                  <Textarea
                                    id={`review-content-${booking.id}`}
                                    value={reviewForm.content}
                                    onChange={(event) =>
                                      setReviewForm((current) => ({ ...current, content: event.target.value }))
                                    }
                                    placeholder="How did the job go?"
                                  />
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                  <div className="space-y-1.5">
                                    <Label htmlFor={`review-name-${booking.id}`}>Your name</Label>
                                    <Input
                                      id={`review-name-${booking.id}`}
                                      value={reviewForm.name}
                                      onChange={(event) =>
                                        setReviewForm((current) => ({ ...current, name: event.target.value }))
                                      }
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label htmlFor={`review-location-${booking.id}`}>Location</Label>
                                    <Input
                                      id={`review-location-${booking.id}`}
                                      value={reviewForm.location}
                                      onChange={(event) =>
                                        setReviewForm((current) => ({ ...current, location: event.target.value }))
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => submitReview(booking)}
                                    disabled={submitReviewMutation.isPending || reviewForm.content.trim().length < 10}
                                  >
                                    {submitReviewMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Submit review
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={submitReviewMutation.isPending}
                                    onClick={() => setReviewingId(null)}
                                  >
                                    <X className="mr-2 h-4 w-4" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => startReview(booking)}>
                                <Star className="mr-2 h-4 w-4" />
                                Leave a review
                              </Button>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="enquiries" className="grid gap-4 md:grid-cols-2">
                {enquiries.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-muted-foreground">No enquiries yet.</CardContent>
                  </Card>
                ) : (
                  enquiries.map((enquiry) => {
                    const canAct = !['converted', 'cancelled'].includes(enquiry.status);
                    return (
                      <Card key={enquiry.id}>
                        <CardHeader>
                          <CardTitle className="text-lg">{enquiry.subject || 'Enquiry'}</CardTitle>
                          <CardDescription>{format(new Date(enquiry.created_at), 'dd MMM yyyy')}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <Badge variant="secondary" className="capitalize">{enquiry.status}</Badge>
                          {enquiry.additional_info && <p className="text-sm text-muted-foreground">{enquiry.additional_info}</p>}
                          {canAct && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setCancelTarget({ kind: 'enquiry', id: enquiry.id, label: enquiry.subject || 'Enquiry' })
                              }
                            >
                              Cancel enquiry
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </TabsContent>

              <TabsContent value="messages" className="grid gap-4 lg:grid-cols-[320px_1fr]">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <MessageSquare className="h-5 w-5" />
                      Threads
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {threads.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No messages yet.</p>
                    ) : (
                      threads.map((thread) => (
                        <button
                          key={thread.id}
                          type="button"
                          onClick={() => setSelectedThreadId(thread.id)}
                          className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${
                            activeThread?.id === thread.id ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                          }`}
                        >
                          <p className="font-medium">{thread.subject || 'Message thread'}</p>
                          <p className="text-xs text-muted-foreground">
                            {thread.booking_id ? 'Booking' : 'Enquiry'} · {format(new Date(thread.created_at), 'dd MMM yyyy')}
                          </p>
                        </button>
                      ))
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{activeThread?.subject || 'Messages'}</CardTitle>
                    <CardDescription>Replies stay attached to this enquiry or booking.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="min-h-48 space-y-3 rounded-md border bg-muted/20 p-4">
                      {activeMessages.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No messages in this thread yet.</p>
                      ) : (
                        activeMessages.map((message) => (
                          <div
                            key={message.id}
                            className={`max-w-[85%] rounded-lg p-3 text-sm ${
                              message.sender_type === 'customer'
                                ? 'ml-auto bg-primary text-primary-foreground'
                                : 'bg-card'
                            }`}
                          >
                            <p>{message.body}</p>
                            <p className="mt-1 text-xs opacity-70">{format(new Date(message.created_at), 'dd MMM, HH:mm')}</p>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="customer-reply">Reply</Label>
                      <Textarea
                        id="customer-reply"
                        value={reply}
                        onChange={(event) => setReply(event.target.value)}
                        placeholder="Write a message..."
                        disabled={!activeThread}
                      />
                    </div>
                    <Button onClick={sendMessage} disabled={!activeThread || sendMessageMutation.isPending || !reply.trim()}>
                      {sendMessageMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                      Send message
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="profile">
                <Card className="max-w-2xl">
                  <CardHeader>
                    <CardTitle>Contact details</CardTitle>
                    <CardDescription>
                      Keep your basic contact and service-location details up to date.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="profile-name">Name</Label>
                        <Input
                          id="profile-name"
                          value={profileForm.name}
                          onChange={(event) => setProfileForm((current) => ({ ...current, name: event.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="profile-phone">Phone</Label>
                        <Input
                          id="profile-phone"
                          value={profileForm.phone}
                          onChange={(event) => setProfileForm((current) => ({ ...current, phone: event.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-address">Service address</Label>
                      <Textarea
                        id="profile-address"
                        value={profileForm.address}
                        onChange={(event) => setProfileForm((current) => ({ ...current, address: event.target.value }))}
                      />
                    </div>
                    <Button onClick={saveProfile} disabled={profileSaving || !profileForm.name.trim()}>
                      {profileSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save details
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>

      <AlertDialog
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancel this {cancelTarget?.kind === 'enquiry' ? 'enquiry' : 'booking'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This cancels "{cancelTarget?.label}" with {businessName}. This can't be undone — you'd need to
              get in touch to arrange a new {cancelTarget?.kind === 'enquiry' ? 'enquiry' : 'booking'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionPending}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                confirmCancellation();
              }}
              disabled={actionPending}
            >
              {actionPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes, cancel {cancelTarget?.kind === 'enquiry' ? 'enquiry' : 'booking'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default CustomerDashboard;
